import React, { useEffect, useState } from "react";
import * as yup from "yup";
import { useGet, usePost } from "../../api";
import {
  checkoutTechnologyType,
  IMerchantSimple,
  IUpsertMerchant,
} from "../../interfaces";
import { useFormik } from "formik";
import { useNavigate, useParams } from "react-router";
import {
  Button,
  Col,
  Form,
  FormControl,
  FormGroup,
  FormLabel,
  Row,
  Collapse,
  Card,
} from "react-bootstrap";
import { SelectorsSettingsModal } from "./selectors-settings-modal";

const validationSchema = yup.object({
  name: yup.string().required(),
  checkoutType: yup
    .string()
    .required()
    .oneOf(Object.values(checkoutTechnologyType)),
  countryCode: yup.string().required(),
  homePageUrl: yup.string().required("Home page URL is required"),
  recommendedTestProducts: yup.array().of(yup.string()).optional(),
  cachedProductPages: yup
    .array()
    .of(
      yup.object({
        url: yup
          .string()
          .url("Must be a valid URL")
          .required("URL is required"),
        skipSkuCheck: yup.boolean().required(),
      })
    )
    .optional(),
  additionalSettings: yup
    .object({
      hasSearchToggle: yup.boolean().optional(),
      knownCartPage: yup.string().optional(),
      cartPageIsCheckout: yup.boolean().optional(),
      cookieDismissalRequired: yup.boolean().optional(),
      newsletterDismissalRequired: yup.boolean().optional(),
      regionalDismissalRequired: yup.boolean().optional(),
      continueOnCouponFailure: yup.boolean().optional(),
      ignoreSiteSuitability: yup.boolean().optional(),
      mapDevice: yup.string().optional(),
      journeyTestEnabled: yup.boolean().optional(),
    })
    .optional(),
  schedule: yup
    .object({
      enabled: yup.boolean().optional(),
      cronExpression: yup.string().optional(),
      timezone: yup.string().optional(),
      nextScheduleRun: yup.date().optional(),
      auditCronExpression: yup.string().optional(),
      auditNextScheduleRun: yup.date().nullable().optional(),
    })
    .optional(),
  wooCommerceMetadata: yup
    .object({
      referer: yup.string().optional(),
      origin: yup.string().optional(),
      applyCouponNonce: yup.string().optional(),
      removeCouponNonce: yup.string().optional(),
      cookies: yup.string().optional(),
      type: yup.string().oneOf(["v1", "v2"]).optional(),
    })
    .optional(),
  bigCommerceMetadata: yup
    .object({
      xsrfToken: yup.string().optional(),
      cookies: yup.string().optional(),
      referer: yup.string().optional(),
      origin: yup.string().optional(),
    })
    .optional(),
  shopifyMetadata: yup
    .object({
      sessionToken: yup.string().optional(),
      referer: yup.string().optional(),
      origin: yup.string().optional(),
      cookies: yup.string().optional(),
    })
    .optional(),
  magentoMetadata: yup
    .object({
      formKey: yup.string().optional(),
      referer: yup.string().optional(),
      origin: yup.string().optional(),
      cookies: yup.string().optional(),
      postUrl: yup.string().optional(),
      userAgent: yup.string().optional(),
    })
    .optional(),
  salesforceMetadata: yup
    .object({
      referer: yup.string().optional(),
      origin: yup.string().optional(),
      cookies: yup.string().optional(),
      csrfToken: yup.string().optional(),
      couponPostUrl: yup.string().optional(),
      userAgent: yup.string().optional(),
    })
    .optional(),
  discoveredSelectors: yup.object().optional(),
});

export const AddUpdateMerchant: React.FC = () => {
  const { _id } = useParams<{ _id?: string }>();
  const upsertMerchant = usePost<string, IUpsertMerchant>();
  const merchant = useGet<IMerchantSimple>(_id ? `/merchants/${_id}` : null);
  const navigate = useNavigate();
  const [showProducts, setShowProducts] = useState(false);
  const [showCookiesPopups, setShowCookiesPopups] = useState(false);
  const [showCartSettings, setShowCartSettings] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [showWooCommerce, setShowWooCommerce] = useState(false);
  const [showBigCommerce, setShowBigCommerce] = useState(false);
  const [showShopify, setShowShopify] = useState(false);
  const [showMagento, setShowMagento] = useState(false);
  const [showSalesforce, setShowSalesforce] = useState(false);
  const [showSelectorsModal, setShowSelectorsModal] = useState(false);
  const formik = useFormik({
    validationSchema,
    initialValues: {
      name: "",
      checkoutType: checkoutTechnologyType.OTHER,
      runMode: "" as "" | "agentic" | "api" | "session",
      countryCode: "",
      homePageUrl: "",
      recommendedTestProducts: [""] as string[],
      cachedProductPages: [] as Array<{
        url: string;
        skipSkuCheck: boolean;
      }>,
      additionalSettings: {
        hasSearchToggle: false,
        knownCartPage: "",
        cartPageIsCheckout: false,
        cookieDismissalRequired: false,
        newsletterDismissalRequired: false,
        regionalDismissalRequired: false,
        continueOnCouponFailure: false,
        ignoreSiteSuitability: false,
        mapDevice: "",
        journeyTestEnabled: true,
      },
      schedule: {
        enabled: false,
        cronExpression: "0 0 * * *",
        timezone: "UTC",
        nextScheduleRun: new Date(),
        auditCronExpression: "",
        auditNextScheduleRun: null as Date | null,
      },
      wooCommerceMetadata: {
        referer: "",
        origin: "",
        applyCouponNonce: "",
        removeCouponNonce: "",
        cookies: "",
        type: "v1",
      },
      bigCommerceMetadata: {
        xsrfToken: "",
        cookies: "",
        referer: "",
        origin: "",
      },
      shopifyMetadata: {
        sessionToken: "",
        referer: "",
        origin: "",
        cookies: "",
      },
      magentoMetadata: {
        formKey: "",
        referer: "",
        origin: "",
        cookies: "",
        postUrl: "",
        userAgent: "",
      },
      salesforceMetadata: {
        referer: "",
        origin: "",
        cookies: "",
        csrfToken: "",
        couponPostUrl: "",
        userAgent: "",
      },
      discoveredSelectors: {},
    },

    onSubmit: async (values) => {
      const cleanedValues: any = {
        ...values,
        // "" = Auto → null so the backend infers from checkoutType
        runMode: values.runMode || null,
        recommendedTestProducts: values.recommendedTestProducts.filter(
          (product) => product.trim() !== ""
        ),
        cachedProductPages: values.cachedProductPages.filter(
          (page) => page.url.trim() !== ""
        ),
        countryCode:
          values.countryCode.trim() !== "" ? values.countryCode : undefined,
        additionalSettings: {
          ...values.additionalSettings,
          // "" = default (mobile, in code) -> null so the backend applies it
          mapDevice: values.additionalSettings.mapDevice || null,
        },
      };

      console.log(
        merchant.data?.schedule?.nextScheduleRun,
        values.schedule.nextScheduleRun.toISOString()
      );
      if (merchant.data?.schedule?.nextScheduleRun) {
        let scheduleBefore = new Date(merchant.data?.schedule?.nextScheduleRun);
        let scheduleAfter = new Date(values.schedule.nextScheduleRun);
        if (scheduleBefore.getTime() === scheduleAfter.getTime()) {
          cleanedValues.schedule.nextScheduleRun = undefined;
        }
      }

      upsertMerchant.execute(
        `/merchants${_id ? `/${_id}` : ""}`,
        cleanedValues
      );
      navigate("/merchants");
    },
  });

  console.log("Formik errors:", formik.errors);

  useEffect(() => {
    if (!merchant.loading && merchant.data) {
      formik.setValues({
        name: merchant.data?.name || "",
        checkoutType:
          merchant.data.checkoutType || checkoutTechnologyType.OTHER,
        runMode: ((merchant.data as any)?.runMode ?? "") as
          | ""
          | "agentic"
          | "api"
          | "session",
        countryCode: merchant.data?.countryCode ?? "",
        homePageUrl: merchant.data?.homePageUrl || "",
        recommendedTestProducts: merchant.data?.recommendedTestProducts?.length
          ? merchant.data.recommendedTestProducts
          : [],
        cachedProductPages: merchant.data?.cachedProductPages?.length
          ? merchant.data.cachedProductPages.map((page) => ({
              url: page.url,
              skipSkuCheck: page.skipSkuCheck,
            }))
          : [],
        additionalSettings: {
          hasSearchToggle:
            merchant.data?.additionalSettings?.hasSearchToggle || false,
          knownCartPage: merchant.data?.additionalSettings?.knownCartPage || "",
          cartPageIsCheckout:
            merchant.data?.additionalSettings?.cartPageIsCheckout || false,
          cookieDismissalRequired:
            merchant.data?.additionalSettings?.cookieDismissalRequired || false,
          newsletterDismissalRequired:
            merchant.data?.additionalSettings?.newsletterDismissalRequired ||
            false,
          regionalDismissalRequired:
            merchant.data?.additionalSettings?.regionalDismissalRequired ||
            false,
          continueOnCouponFailure:
            merchant.data?.additionalSettings?.continueOnCouponFailure || false,
          ignoreSiteSuitability:
            merchant.data?.additionalSettings?.ignoreSiteSuitability || false,
          mapDevice:
            ((merchant.data?.additionalSettings as any)?.mapDevice as string) ||
            "",
          journeyTestEnabled:
            (merchant.data?.additionalSettings as any)?.journeyTestEnabled !==
            false,
        },
        schedule: {
          enabled: merchant.data?.schedule?.enabled || false,
          cronExpression:
            merchant.data?.schedule?.cronExpression || "0 0 * * *",
          timezone: merchant.data?.schedule?.timezone || "UTC",
          nextScheduleRun: merchant.data?.schedule?.nextScheduleRun
            ? new Date(merchant.data.schedule.nextScheduleRun)
            : new Date(),
          auditCronExpression:
            merchant.data?.schedule?.auditCronExpression || "",
          auditNextScheduleRun: merchant.data?.schedule?.auditNextScheduleRun
            ? new Date(merchant.data.schedule.auditNextScheduleRun)
            : null,
        },
        wooCommerceMetadata: {
          referer: merchant.data?.wooCommerceMetadata?.referer || "",
          origin: merchant.data?.wooCommerceMetadata?.origin || "",
          applyCouponNonce:
            merchant.data?.wooCommerceMetadata?.applyCouponNonce || "",
          removeCouponNonce:
            merchant.data?.wooCommerceMetadata?.removeCouponNonce || "",
          cookies: merchant.data?.wooCommerceMetadata?.cookies || "",
          type: merchant.data?.wooCommerceMetadata?.type || "v1",
        },
        bigCommerceMetadata: {
          xsrfToken: merchant.data?.bigCommerceMetadata?.xsrfToken || "",
          cookies: merchant.data?.bigCommerceMetadata?.cookies || "",
          referer: merchant.data?.bigCommerceMetadata?.referer || "",
          origin: merchant.data?.bigCommerceMetadata?.origin || "",
        },
        shopifyMetadata: {
          sessionToken: merchant.data?.shopifyMetadata?.sessionToken || "",
          referer: merchant.data?.shopifyMetadata?.referer || "",
          origin: merchant.data?.shopifyMetadata?.origin || "",
          cookies: merchant.data?.shopifyMetadata?.cookies || "",
        },
        magentoMetadata: {
          formKey: merchant.data?.magentoMetadata?.formKey || "",
          referer: merchant.data?.magentoMetadata?.referer || "",
          origin: merchant.data?.magentoMetadata?.origin || "",
          cookies: merchant.data?.magentoMetadata?.cookies || "",
          postUrl: merchant.data?.magentoMetadata?.postUrl || "",
          userAgent: merchant.data?.magentoMetadata?.userAgent || "",
        },
        salesforceMetadata: {
          referer: merchant.data?.salesforceMetadata?.referer || "",
          origin: merchant.data?.salesforceMetadata?.origin || "",
          cookies: merchant.data?.salesforceMetadata?.cookies || "",
          csrfToken: merchant.data?.salesforceMetadata?.csrfToken || "",
          couponPostUrl: merchant.data?.salesforceMetadata?.couponPostUrl || "",
          userAgent: merchant.data?.salesforceMetadata?.userAgent || "",
        },
        discoveredSelectors: merchant.data?.discoveredSelectors || {},
      });
    }
  }, [merchant?.loading]);

  const renderArrayField = (
    fieldName: keyof typeof formik.values,
    label: string,
    placeholder: string
  ) => {
    const values = formik.values[fieldName] as string[];
    return (
      <FormGroup className="mt-3">
        <FormLabel>{label}</FormLabel>
        {values.map((value, index) => (
          <div key={index} className="d-flex mb-2">
            <FormControl
              value={value}
              onChange={(e) => {
                const newValues = [...values];
                newValues[index] = e.target.value;
                formik.setFieldValue(fieldName, newValues);
              }}
              placeholder={placeholder}
            />
            <Button
              variant="outline-danger"
              size="sm"
              className="ms-2"
              onClick={() => {
                const newValues = values.filter((_, i) => i !== index);
                formik.setFieldValue(fieldName, newValues);
              }}
            >
              Remove
            </Button>
          </div>
        ))}
        <Button
          variant="outline-primary"
          size="sm"
          onClick={() => {
            formik.setFieldValue(fieldName, [...values, ""]);
          }}
        >
          Add {label.slice(0, -1)}
        </Button>
      </FormGroup>
    );
  };

  const renderCachedProductPages = () => {
    const pages = formik.values.cachedProductPages;
    return (
      <FormGroup className="mt-3">
        <FormLabel>Cached Product Pages</FormLabel>
        {pages.map((page, index) => (
          <div key={index} className="border rounded p-3 mb-3">
            <div className="row">
              <div className="col-md-8">
                <FormGroup className="mb-2">
                  <FormLabel>URL</FormLabel>
                  <FormControl
                    value={page.url}
                    onChange={(e) => {
                      const newPages = [...pages];
                      newPages[index] = {
                        ...newPages[index],
                        url: e.target.value,
                      };
                      formik.setFieldValue("cachedProductPages", newPages);
                    }}
                    placeholder="https://example.com/product"
                    isInvalid={
                      formik.touched.cachedProductPages?.[index]?.url &&
                      !!(formik.errors.cachedProductPages as any)?.[index]?.url
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {(formik.errors.cachedProductPages as any)?.[index]?.url}
                  </Form.Control.Feedback>
                </FormGroup>
              </div>
              <div className="col-md-4">
                <FormGroup className="mb-2">
                  <FormLabel>Skip SKU Check</FormLabel>
                  <div className="form-check mt-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`skipSkuCheck-${index}`}
                      checked={page.skipSkuCheck}
                      onChange={(e) => {
                        const newPages = [...pages];
                        newPages[index] = {
                          ...newPages[index],
                          skipSkuCheck: e.target.checked,
                        };
                        formik.setFieldValue("cachedProductPages", newPages);
                      }}
                    />
                    <label
                      className="form-check-label"
                      htmlFor={`skipSkuCheck-${index}`}
                    >
                      Skip SKU check for this product page
                    </label>
                  </div>
                </FormGroup>
              </div>
            </div>
            <div className="d-flex justify-content-end">
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => {
                  const newPages = pages.filter((_, i) => i !== index);
                  formik.setFieldValue("cachedProductPages", newPages);
                }}
              >
                Remove Page
              </Button>
            </div>
          </div>
        ))}
        <Button
          variant="outline-primary"
          size="sm"
          onClick={() => {
            formik.setFieldValue("cachedProductPages", [
              ...pages,
              { url: "", skipSkuCheck: false },
            ]);
          }}
        >
          Add Product Page
        </Button>
      </FormGroup>
    );
  };

  return (
    <div className="container-fluid">
      {/* Toolbar with Action Buttons */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3>{_id ? `Update` : `Add`} Merchant</h3>
        <div className="d-flex gap-2">
          {merchant.data && (
            <>
              <Button
                variant="outline-primary"
                onClick={() => setShowSelectorsModal(true)}
                className="d-flex align-items-center"
              >
                <i className="bi bi-code-slash me-1"></i> Selectors
              </Button>
              {/* Add more action buttons here in the future */}
            </>
          )}
        </div>
      </div>

      <Row>
        {/* Left Column - Main Fields */}
        <Col md={6}>
          <Form noValidate onSubmit={formik.handleSubmit}>
            {/* Essential Fields */}
            <FormGroup className="mt-3">
              <FormLabel htmlFor="name">Name</FormLabel>
              <FormControl
                name="name"
                value={formik.values.name}
                onChange={formik.handleChange}
                placeholder="Merchant Name"
                isInvalid={formik.touched.name && !!formik.errors.name}
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.name}
              </Form.Control.Feedback>
            </FormGroup>

            <FormGroup className="mt-3">
              <FormLabel htmlFor="countryCode">Country Code</FormLabel>
              <FormControl
                name="countryCode"
                value={formik.values.countryCode}
                onChange={formik.handleChange}
                placeholder="Merchant Country Code"
                isInvalid={
                  formik.touched.countryCode && !!formik.errors.countryCode
                }
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.countryCode}
              </Form.Control.Feedback>
            </FormGroup>

            <FormGroup className="mt-3">
              <FormLabel htmlFor="homePageUrl">Home Page URL</FormLabel>
              <FormControl
                name="homePageUrl"
                value={formik.values.homePageUrl}
                onChange={formik.handleChange}
                placeholder="https://example.com"
                isInvalid={
                  formik.touched.homePageUrl && !!formik.errors.homePageUrl
                }
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.homePageUrl}
              </Form.Control.Feedback>
            </FormGroup>

            <FormGroup className="mt-3">
              <FormLabel htmlFor="checkoutType">
                Checkout Technology Type
              </FormLabel>
              <FormControl
                as="select"
                name="checkoutType"
                value={formik.values.checkoutType}
                onChange={formik.handleChange}
                isInvalid={
                  formik.touched.checkoutType && !!formik.errors.checkoutType
                }
              >
                {Object.values(checkoutTechnologyType).map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </FormControl>
              <Form.Control.Feedback type="invalid">
                {formik.errors.checkoutType}
              </Form.Control.Feedback>
            </FormGroup>

            <FormGroup className="mt-3">
              <Form.Check
                type="switch"
                id="journeyTestEnabled"
                name="additionalSettings.journeyTestEnabled"
                label="Daily checkout test"
                checked={
                  formik.values.additionalSettings.journeyTestEnabled !== false
                }
                onChange={formik.handleChange}
              />
              <Form.Text className="text-muted">
                Check this merchant's checkout every day and flag anything that
                stops working. Needs a saved product page.
              </Form.Text>
            </FormGroup>

            <FormGroup className="mt-3">
              <FormLabel htmlFor="mapDevice">Map Device</FormLabel>
              <FormControl
                as="select"
                name="additionalSettings.mapDevice"
                value={formik.values.additionalSettings.mapDevice}
                onChange={formik.handleChange}
              >
                <option value="">Default (mobile)</option>
                <option value="mobile">Mobile</option>
                <option value="desktop">Desktop</option>
              </FormControl>
              <Form.Text className="text-muted">
                Device the merchant-map build/test emulates. Full journey only.
              </Form.Text>
            </FormGroup>

            <FormGroup className="mt-3">
              <FormLabel htmlFor="runMode">Run Mode</FormLabel>
              <FormControl
                as="select"
                name="runMode"
                value={formik.values.runMode}
                onChange={formik.handleChange}
              >
                <option value="">
                  Auto — inferred from checkout type
                  {formik.values.checkoutType === checkoutTechnologyType.OTHER
                    ? " (agentic)"
                    : " (api)"}
                </option>
                <option value="agentic">Agentic (browser navigation)</option>
                <option value="api">API (platform handlers)</option>
                <option
                  value="session"
                  disabled={!(merchant?.data as any)?.sessionRunId}
                >
                  Session (replay captured session)
                  {!(merchant?.data as any)?.sessionRunId &&
                    " — no captured session yet"}
                </option>
              </FormControl>
              <Form.Text className="text-muted">
                How scheduled runs execute. Automatically set to Session when a
                captured session is linked; override here if needed. Session
                requires a captured session (run the extension flow first).
              </Form.Text>
            </FormGroup>

            <Button
              type="submit"
              className="mt-4"
              variant="primary"
              disabled={formik.isSubmitting}
            >
              {_id ? `Update` : `Add`} Merchant
            </Button>
          </Form>
        </Col>

        {/* Right Column - Products Section */}
        <Col md={6}>
          <div className="mt-5">
            {/* Products Section */}
            <Card className="mb-3">
              <Card.Header>
                <Button
                  variant="link"
                  onClick={() => setShowProducts(!showProducts)}
                  aria-controls="products-settings"
                  aria-expanded={showProducts}
                  className="text-decoration-none p-0 text-start w-100"
                >
                  <h6 className="mb-0">
                    Products & Testing
                    <i
                      className={`bi bi-chevron-${
                        showProducts ? "up" : "down"
                      } float-end`}
                    ></i>
                  </h6>
                </Button>
              </Card.Header>
              <Collapse in={showProducts}>
                <Card.Body id="products-settings">
                  <FormGroup className="mt-3">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="hasSearchToggle"
                        checked={
                          formik.values.additionalSettings.hasSearchToggle
                        }
                        onChange={(e) => {
                          formik.setFieldValue(
                            "additionalSettings.hasSearchToggle",
                            e.target.checked
                          );
                        }}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="hasSearchToggle"
                      >
                        Has Search Toggle
                      </label>
                    </div>
                    <small className="form-text text-muted">
                      Check if this merchant requires toggling the search
                      interface before searching
                    </small>
                  </FormGroup>

                  {renderArrayField(
                    "recommendedTestProducts",
                    "Recommended Test Products",
                    "product/keyword"
                  )}

                  {renderCachedProductPages()}
                </Card.Body>
              </Collapse>
            </Card>

            {/* Cart Settings Section */}
            <Card className="mb-3">
              <Card.Header>
                <Button
                  variant="link"
                  onClick={() => setShowCartSettings(!showCartSettings)}
                  aria-controls="cart-settings"
                  aria-expanded={showCartSettings}
                  className="text-decoration-none p-0 text-start w-100"
                >
                  <h6 className="mb-0">
                    Cart Settings
                    <i
                      className={`bi bi-chevron-${
                        showCartSettings ? "up" : "down"
                      } float-end`}
                    ></i>
                  </h6>
                </Button>
              </Card.Header>
              <Collapse in={showCartSettings}>
                <Card.Body id="cart-settings">
                  <FormGroup className="mt-3">
                    <FormLabel htmlFor="knownCartPage">
                      Known Cart Page URL
                    </FormLabel>
                    <FormControl
                      name="knownCartPage"
                      value={formik.values.additionalSettings.knownCartPage}
                      onChange={(e) => {
                        formik.setFieldValue(
                          "additionalSettings.knownCartPage",
                          e.target.value
                        );
                      }}
                      placeholder="https://example.com/cart"
                    />
                    <small className="form-text text-muted">
                      Specify the direct URL to the cart page if known
                    </small>
                  </FormGroup>

                  <FormGroup className="mt-3">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="cartPageIsCheckout"
                        checked={
                          formik.values.additionalSettings.cartPageIsCheckout
                        }
                        onChange={(e) => {
                          formik.setFieldValue(
                            "additionalSettings.cartPageIsCheckout",
                            e.target.checked
                          );
                        }}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="cartPageIsCheckout"
                      >
                        Cart Page Is Checkout
                      </label>
                    </div>
                    <small className="form-text text-muted">
                      Check if the cart page serves as the checkout page (no
                      separate checkout step)
                    </small>
                  </FormGroup>
                  <FormGroup className="mt-3">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="ignoreSiteSuitability"
                        checked={
                          formik.values.additionalSettings.ignoreSiteSuitability
                        }
                        onChange={(e) => {
                          formik.setFieldValue(
                            "additionalSettings.ignoreSiteSuitability",
                            e.target.checked
                          );
                        }}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="ignoreSiteSuitability"
                      >
                        Run even if not suitable
                      </label>
                    </div>
                    <small className="form-text text-muted">
                      The suitability check classes some sites (travel,
                      insurance, …) as ones we don't test, and their runs stop
                      on the homepage. Tick this to run the full walk anyway.
                    </small>
                  </FormGroup>
                  <FormGroup className="mt-3">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="continueOnCouponFailure"
                        checked={
                          formik.values.additionalSettings
                            .continueOnCouponFailure
                        }
                        onChange={(e) => {
                          formik.setFieldValue(
                            "additionalSettings.continueOnCouponFailure",
                            e.target.checked
                          );
                        }}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="continueOnCouponFailure"
                      >
                        Continue On Coupon Failure
                      </label>
                    </div>
                    <small className="form-text text-muted">
                      Check if the process should continue even if coupon
                      application fails
                    </small>
                  </FormGroup>
                </Card.Body>
              </Collapse>
            </Card>

            {/* Cookies & Popups Section */}
            <Card className="mb-3">
              <Card.Header>
                <Button
                  variant="link"
                  onClick={() => setShowCookiesPopups(!showCookiesPopups)}
                  aria-controls="cookies-popups-settings"
                  aria-expanded={showCookiesPopups}
                  className="text-decoration-none p-0 text-start w-100"
                >
                  <h6 className="mb-0">
                    Cookies & Popups
                    <i
                      className={`bi bi-chevron-${
                        showCookiesPopups ? "up" : "down"
                      } float-end`}
                    ></i>
                  </h6>
                </Button>
              </Card.Header>
              <Collapse in={showCookiesPopups}>
                <Card.Body id="cookies-popups-settings">
                  <FormGroup className="mt-3">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="cookieDismissalRequired"
                        checked={
                          formik.values.additionalSettings
                            .cookieDismissalRequired
                        }
                        onChange={(e) => {
                          formik.setFieldValue(
                            "additionalSettings.cookieDismissalRequired",
                            e.target.checked
                          );
                        }}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="cookieDismissalRequired"
                      >
                        Cookie Dismissal Required
                      </label>
                    </div>
                    <small className="form-text text-muted">
                      Check if this merchant requires dismissing cookie consent
                      banners
                    </small>
                  </FormGroup>

                  <FormGroup className="mt-3">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="newsletterDismissalRequired"
                        checked={
                          formik.values.additionalSettings
                            .newsletterDismissalRequired
                        }
                        onChange={(e) => {
                          formik.setFieldValue(
                            "additionalSettings.newsletterDismissalRequired",
                            e.target.checked
                          );
                        }}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="newsletterDismissalRequired"
                      >
                        Newsletter Dismissal Required
                      </label>
                    </div>
                    <small className="form-text text-muted">
                      Check if this merchant requires dismissing newsletter
                      signup popups
                    </small>
                  </FormGroup>

                  <FormGroup className="mt-3">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="regionalDismissalRequired"
                        checked={
                          formik.values.additionalSettings
                            .regionalDismissalRequired
                        }
                        onChange={(e) => {
                          formik.setFieldValue(
                            "additionalSettings.regionalDismissalRequired",
                            e.target.checked
                          );
                        }}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="regionalDismissalRequired"
                      >
                        Regional Dismissal Required
                      </label>
                    </div>
                    <small className="form-text text-muted">
                      Check if this merchant requires dismissing
                      regional/country selection popups
                    </small>
                  </FormGroup>
                </Card.Body>
              </Collapse>
            </Card>

            {/* Schedule Section */}
            <Card className="mb-3">
              <Card.Header>
                <Button
                  variant="link"
                  onClick={() => setShowSchedule(!showSchedule)}
                  aria-controls="schedule-settings"
                  aria-expanded={showSchedule}
                  className="text-decoration-none p-0 text-start w-100"
                >
                  <h6 className="mb-0">
                    Scheduling
                    <i
                      className={`bi bi-chevron-${
                        showSchedule ? "up" : "down"
                      } float-end`}
                    ></i>
                  </h6>
                </Button>
              </Card.Header>
              <Collapse in={showSchedule}>
                <Card.Body id="schedule-settings">
                  <FormGroup className="mt-3">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="scheduleEnabled"
                        checked={formik.values.schedule.enabled}
                        onChange={(e) => {
                          formik.setFieldValue(
                            "schedule.enabled",
                            e.target.checked
                          );
                        }}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="scheduleEnabled"
                      >
                        Enable Scheduled Processing
                      </label>
                    </div>
                    <small className="form-text text-muted">
                      Enable automatic scheduled processing for this merchant
                    </small>
                  </FormGroup>

                  <FormGroup className="mt-3">
                    <FormLabel htmlFor="cronExpression">
                      Cron Expression
                    </FormLabel>
                    <FormControl
                      name="cronExpression"
                      value={formik.values.schedule.cronExpression}
                      onChange={(e) => {
                        formik.setFieldValue(
                          "schedule.cronExpression",
                          e.target.value
                        );
                      }}
                      placeholder="0 0 * * *"
                      disabled={!formik.values.schedule.enabled}
                    />
                    <small className="form-text text-muted">
                      Cron expression for scheduling (e.g., "0 0 * * *" for
                      daily at midnight)
                    </small>
                  </FormGroup>

                  <FormGroup className="mt-3">
                    <FormLabel htmlFor="timezone">Timezone</FormLabel>
                    <FormControl
                      as="select"
                      name="timezone"
                      value={formik.values.schedule.timezone}
                      onChange={(e) => {
                        formik.setFieldValue(
                          "schedule.timezone",
                          e.target.value
                        );
                      }}
                      disabled={!formik.values.schedule.enabled}
                    >
                      <option value="UTC">UTC</option>
                      <option value="America/New_York">Eastern Time</option>
                      <option value="America/Chicago">Central Time</option>
                      <option value="America/Denver">Mountain Time</option>
                      <option value="America/Los_Angeles">Pacific Time</option>
                      <option value="Europe/London">London</option>
                      <option value="Europe/Paris">Paris</option>
                      <option value="Asia/Tokyo">Tokyo</option>
                      <option value="Australia/Sydney">Sydney</option>
                    </FormControl>
                    <small className="form-text text-muted">
                      Timezone for scheduled execution
                    </small>
                  </FormGroup>

                  <FormGroup className="mt-3">
                    <FormLabel htmlFor="nextScheduleRun">
                      Next Scheduled Run
                    </FormLabel>
                    <FormControl
                      type="datetime-local"
                      name="nextScheduleRun"
                      value={
                        formik.values.schedule.nextScheduleRun instanceof Date
                          ? formik.values.schedule.nextScheduleRun
                              .toISOString()
                              .slice(0, 16)
                          : ""
                      }
                      onChange={(e) => {
                        const dateValue = e.target.value
                          ? new Date(e.target.value)
                          : new Date();
                        formik.setFieldValue(
                          "schedule.nextScheduleRun",
                          dateValue
                        );
                      }}
                      disabled={!formik.values.schedule.enabled}
                    />
                    <small className="form-text text-muted">
                      Set the next execution time. Set to a past date to trigger
                      immediate execution.
                    </small>
                  </FormGroup>

                  <hr className="my-3" />
                  <h6 className="text-muted">
                    Audit Schedule (Pending/Suspended Coupons)
                  </h6>

                  <FormGroup className="mt-3">
                    <FormLabel htmlFor="auditCronExpression">
                      Audit Cron Expression
                    </FormLabel>
                    <FormControl
                      name="auditCronExpression"
                      value={formik.values.schedule.auditCronExpression || ""}
                      onChange={(e) => {
                        formik.setFieldValue(
                          "schedule.auditCronExpression",
                          e.target.value
                        );
                      }}
                      placeholder="0 6 * * *"
                      disabled={!formik.values.schedule.enabled}
                    />
                    <small className="form-text text-muted">
                      Cron expression for audit runs (e.g., "0 6 * * *" for
                      daily at 6 AM). Leave empty to disable audit scheduling.
                    </small>
                  </FormGroup>

                  <FormGroup className="mt-3">
                    <FormLabel htmlFor="auditNextScheduleRun">
                      Next Audit Run
                    </FormLabel>
                    <FormControl
                      type="datetime-local"
                      name="auditNextScheduleRun"
                      value={
                        formik.values.schedule.auditNextScheduleRun instanceof
                        Date
                          ? formik.values.schedule.auditNextScheduleRun
                              .toISOString()
                              .slice(0, 16)
                          : ""
                      }
                      onChange={(e) => {
                        const dateValue = e.target.value
                          ? new Date(e.target.value)
                          : null;
                        formik.setFieldValue(
                          "schedule.auditNextScheduleRun",
                          dateValue
                        );
                      }}
                      disabled={
                        !formik.values.schedule.enabled ||
                        !formik.values.schedule.auditCronExpression
                      }
                    />
                    <small className="form-text text-muted">
                      Next execution time for the audit run.
                    </small>
                  </FormGroup>
                </Card.Body>
              </Collapse>
            </Card>

            {/* WooCommerce Metadata Section */}
            {formik.values.checkoutType ===
              checkoutTechnologyType.WOOCOMMERCE && (
              <Card className="mb-3">
                <Card.Header>
                  <Button
                    variant="link"
                    aria-controls="woocommerce-settings"
                    aria-expanded={showWooCommerce}
                    className="text-decoration-none p-0 text-start w-100"
                    onClick={() => setShowWooCommerce(!showWooCommerce)}
                  >
                    <h6 className="mb-0">
                      WooCommerce Metadata
                      <i
                        className={`bi bi-chevron-${
                          showWooCommerce ? "up" : "down"
                        } float-end`}
                      ></i>
                    </h6>
                  </Button>
                </Card.Header>
                <Collapse in={showWooCommerce}>
                  <Card.Body id="woocommerce-settings">
                    <FormGroup className="mt-3">
                      <FormLabel htmlFor="wooCommerceReferer">
                        Referer
                      </FormLabel>
                      <FormControl
                        name="wooCommerceReferer"
                        value={formik.values.wooCommerceMetadata.referer}
                        onChange={(e) => {
                          formik.setFieldValue(
                            "wooCommerceMetadata.referer",
                            e.target.value
                          );
                        }}
                        placeholder="https://example.com/checkout"
                      />
                      <small className="form-text text-muted">
                        The referer URL for WooCommerce requests
                      </small>
                    </FormGroup>

                    <FormGroup className="mt-3">
                      <FormLabel htmlFor="wooCommerceOrigin">Origin</FormLabel>
                      <FormControl
                        name="wooCommerceOrigin"
                        value={formik.values.wooCommerceMetadata.origin}
                        onChange={(e) => {
                          formik.setFieldValue(
                            "wooCommerceMetadata.origin",
                            e.target.value
                          );
                        }}
                        placeholder="https://example.com"
                      />
                      <small className="form-text text-muted">
                        The origin URL for WooCommerce requests
                      </small>
                    </FormGroup>

                    <FormGroup className="mt-3">
                      <FormLabel htmlFor="wooCommerceNonce">
                        Apply Coupon Nonce
                      </FormLabel>
                      <FormControl
                        name="wooCommerceNonce"
                        value={
                          formik.values.wooCommerceMetadata.applyCouponNonce
                        }
                        onChange={(e) => {
                          formik.setFieldValue(
                            "wooCommerceMetadata.applyCouponNonce",
                            e.target.value
                          );
                        }}
                        placeholder="abc123def456"
                      />
                      <small className="form-text text-muted">
                        The nonce token required for applying coupons
                      </small>
                    </FormGroup>

                    <FormGroup className="mt-3">
                      <FormLabel htmlFor="wooCommerceNonce">
                        Remove Coupon Nonce
                      </FormLabel>
                      <FormControl
                        name="wooCommerceNonce"
                        value={
                          formik.values.wooCommerceMetadata.removeCouponNonce
                        }
                        onChange={(e) => {
                          formik.setFieldValue(
                            "wooCommerceMetadata.removeCouponNonce",
                            e.target.value
                          );
                        }}
                        placeholder="abc123def456"
                      />
                      <small className="form-text text-muted">
                        The nonce token required for removing coupons
                      </small>
                    </FormGroup>

                    <FormGroup className="mt-3">
                      <FormLabel htmlFor="wooCommerceCookies">
                        Cookies
                      </FormLabel>
                      <FormControl
                        as="textarea"
                        rows={3}
                        name="wooCommerceCookies"
                        value={formik.values.wooCommerceMetadata.cookies}
                        onChange={(e) => {
                          formik.setFieldValue(
                            "wooCommerceMetadata.cookies",
                            e.target.value
                          );
                        }}
                        placeholder="session_id=abc123; cart_hash=def456"
                      />
                      <small className="form-text text-muted">
                        Session cookies required for WooCommerce requests
                      </small>
                    </FormGroup>

                    <FormGroup className="mt-3">
                      <FormLabel htmlFor="wooCommerceType">
                        WooCommerce Version
                      </FormLabel>
                      <FormControl
                        as="select"
                        name="wooCommerceType"
                        value={formik.values.wooCommerceMetadata.type}
                        onChange={(e) => {
                          formik.setFieldValue(
                            "wooCommerceMetadata.type",
                            e.target.value
                          );
                        }}
                      >
                        <option value="v1">Version 1</option>
                        <option value="v2">Version 2</option>
                      </FormControl>
                      <small className="form-text text-muted">
                        Select the WooCommerce API version to use
                      </small>
                    </FormGroup>
                  </Card.Body>
                </Collapse>
              </Card>
            )}

            {/* BigCommerce Metadata Section */}
            {formik.values.checkoutType ===
              checkoutTechnologyType.BIGCOMMERCE && (
              <Card className="mb-3">
                <Card.Header>
                  <Button
                    variant="link"
                    aria-controls="bigcommerce-settings"
                    aria-expanded={showBigCommerce}
                    className="text-decoration-none p-0 text-start w-100"
                    onClick={() => setShowBigCommerce(!showBigCommerce)}
                  >
                    <h6 className="mb-0">
                      BigCommerce Metadata
                      <i
                        className={`bi bi-chevron-${
                          showBigCommerce ? "up" : "down"
                        } float-end`}
                      ></i>
                    </h6>
                  </Button>
                </Card.Header>
                <Collapse in={showBigCommerce}>
                  <Card.Body id="bigcommerce-settings">
                    <FormGroup className="mt-3">
                      <FormLabel htmlFor="bigCommerceXsrfToken">
                        XSRF Token
                      </FormLabel>
                      <FormControl
                        name="bigCommerceXsrfToken"
                        value={formik.values.bigCommerceMetadata.xsrfToken}
                        onChange={(e) => {
                          formik.setFieldValue(
                            "bigCommerceMetadata.xsrfToken",
                            e.target.value
                          );
                        }}
                        placeholder="abc123def456"
                      />
                      <small className="form-text text-muted">
                        The XSRF token required for BigCommerce requests
                      </small>
                    </FormGroup>

                    <FormGroup className="mt-3">
                      <FormLabel htmlFor="bigCommerceCookies">
                        Cookies
                      </FormLabel>
                      <FormControl
                        as="textarea"
                        rows={3}
                        name="bigCommerceCookies"
                        value={formik.values.bigCommerceMetadata.cookies}
                        onChange={(e) => {
                          formik.setFieldValue(
                            "bigCommerceMetadata.cookies",
                            e.target.value
                          );
                        }}
                        placeholder="XSRF-TOKEN=abc123; session_id=def456"
                      />
                      <small className="form-text text-muted">
                        Session cookies required for BigCommerce requests
                      </small>
                    </FormGroup>

                    <FormGroup className="mt-3">
                      <FormLabel htmlFor="bigCommerceReferer">
                        Referer
                      </FormLabel>
                      <FormControl
                        name="bigCommerceReferer"
                        value={formik.values.bigCommerceMetadata.referer}
                        onChange={(e) => {
                          formik.setFieldValue(
                            "bigCommerceMetadata.referer",
                            e.target.value
                          );
                        }}
                        placeholder="https://example.com/cart.php"
                      />
                      <small className="form-text text-muted">
                        The referer URL for BigCommerce requests
                      </small>
                    </FormGroup>

                    <FormGroup className="mt-3">
                      <FormLabel htmlFor="bigCommerceOrigin">Origin</FormLabel>
                      <FormControl
                        name="bigCommerceOrigin"
                        value={formik.values.bigCommerceMetadata.origin}
                        onChange={(e) => {
                          formik.setFieldValue(
                            "bigCommerceMetadata.origin",
                            e.target.value
                          );
                        }}
                        placeholder="https://example.com"
                      />
                      <small className="form-text text-muted">
                        The origin URL for BigCommerce requests
                      </small>
                    </FormGroup>
                  </Card.Body>
                </Collapse>
              </Card>
            )}

            {/* Shopify Metadata Section */}
            {formik.values.checkoutType === checkoutTechnologyType.SHOPIFY && (
              <Card className="mb-3">
                <Card.Header>
                  <Button
                    variant="link"
                    aria-controls="shopify-settings"
                    aria-expanded={showShopify}
                    className="text-decoration-none p-0 text-start w-100"
                    onClick={() => setShowShopify(!showShopify)}
                  >
                    <h6 className="mb-0">
                      Shopify Metadata
                      <i
                        className={`bi bi-chevron-${
                          showShopify ? "up" : "down"
                        } float-end`}
                      ></i>
                    </h6>
                  </Button>
                </Card.Header>
                <Collapse in={showShopify}>
                  <Card.Body id="shopify-settings">
                    <FormGroup className="mt-3">
                      <FormLabel htmlFor="shopifySessionToken">
                        Session Token
                      </FormLabel>
                      <FormControl
                        name="shopifySessionToken"
                        value={formik.values.shopifyMetadata.sessionToken}
                        onChange={(e) => {
                          formik.setFieldValue(
                            "shopifyMetadata.sessionToken",
                            e.target.value
                          );
                        }}
                        placeholder="abc123def456"
                      />
                      <small className="form-text text-muted">
                        The session token required for Shopify requests
                      </small>
                    </FormGroup>

                    <FormGroup className="mt-3">
                      <FormLabel htmlFor="shopifyReferer">Referer</FormLabel>
                      <FormControl
                        name="shopifyReferer"
                        value={formik.values.shopifyMetadata.referer}
                        onChange={(e) => {
                          formik.setFieldValue(
                            "shopifyMetadata.referer",
                            e.target.value
                          );
                        }}
                        placeholder="https://example.com/cart"
                      />
                      <small className="form-text text-muted">
                        The referer URL for Shopify requests
                      </small>
                    </FormGroup>

                    <FormGroup className="mt-3">
                      <FormLabel htmlFor="shopifyOrigin">Origin</FormLabel>
                      <FormControl
                        name="shopifyOrigin"
                        value={formik.values.shopifyMetadata.origin}
                        onChange={(e) => {
                          formik.setFieldValue(
                            "shopifyMetadata.origin",
                            e.target.value
                          );
                        }}
                        placeholder="https://example.com"
                      />
                      <small className="form-text text-muted">
                        The origin URL for Shopify requests
                      </small>
                    </FormGroup>

                    <FormGroup className="mt-3">
                      <FormLabel htmlFor="shopifyCookies">Cookies</FormLabel>
                      <FormControl
                        as="textarea"
                        rows={3}
                        name="shopifyCookies"
                        value={formik.values.shopifyMetadata.cookies}
                        onChange={(e) => {
                          formik.setFieldValue(
                            "shopifyMetadata.cookies",
                            e.target.value
                          );
                        }}
                        placeholder="_session_id=abc123; cart=def456"
                      />
                      <small className="form-text text-muted">
                        Session cookies required for Shopify requests
                      </small>
                    </FormGroup>
                  </Card.Body>
                </Collapse>
              </Card>
            )}

            {/* Magento Metadata Section */}
            {formik.values.checkoutType === checkoutTechnologyType.MAGENTO && (
              <Card className="mb-3">
                <Card.Header>
                  <Button
                    variant="link"
                    aria-controls="magento-settings"
                    aria-expanded={showMagento}
                    className="text-decoration-none p-0 text-start w-100"
                    onClick={() => setShowMagento(!showMagento)}
                  >
                    <h6 className="mb-0">
                      Magento Metadata
                      <i
                        className={`bi bi-chevron-${
                          showMagento ? "up" : "down"
                        } float-end`}
                      ></i>
                    </h6>
                  </Button>
                </Card.Header>
                <Collapse in={showMagento}>
                  <Card.Body id="magento-settings">
                    <FormGroup className="mt-3">
                      <FormLabel htmlFor="magentoFormKey">Form Key</FormLabel>
                      <FormControl
                        name="magentoFormKey"
                        value={formik.values.magentoMetadata.formKey}
                        onChange={(e) => {
                          formik.setFieldValue(
                            "magentoMetadata.formKey",
                            e.target.value
                          );
                        }}
                        placeholder="abc123def456"
                      />
                      <small className="form-text text-muted">
                        The form key required for Magento requests
                      </small>
                    </FormGroup>

                    <FormGroup className="mt-3">
                      <FormLabel htmlFor="magentoReferer">Referer</FormLabel>
                      <FormControl
                        name="magentoReferer"
                        value={formik.values.magentoMetadata.referer}
                        onChange={(e) => {
                          formik.setFieldValue(
                            "magentoMetadata.referer",
                            e.target.value
                          );
                        }}
                        placeholder="https://example.com/checkout/cart"
                      />
                      <small className="form-text text-muted">
                        The referer URL for Magento requests
                      </small>
                    </FormGroup>

                    <FormGroup className="mt-3">
                      <FormLabel htmlFor="magentoOrigin">Origin</FormLabel>
                      <FormControl
                        name="magentoOrigin"
                        value={formik.values.magentoMetadata.origin}
                        onChange={(e) => {
                          formik.setFieldValue(
                            "magentoMetadata.origin",
                            e.target.value
                          );
                        }}
                        placeholder="https://example.com"
                      />
                      <small className="form-text text-muted">
                        The origin URL for Magento requests
                      </small>
                    </FormGroup>

                    <FormGroup className="mt-3">
                      <FormLabel htmlFor="magentoCookies">Cookies</FormLabel>
                      <FormControl
                        as="textarea"
                        rows={3}
                        name="magentoCookies"
                        value={formik.values.magentoMetadata.cookies}
                        onChange={(e) => {
                          formik.setFieldValue(
                            "magentoMetadata.cookies",
                            e.target.value
                          );
                        }}
                        placeholder="PHPSESSID=abc123; form_key=def456"
                      />
                      <small className="form-text text-muted">
                        Session cookies required for Magento requests
                      </small>
                    </FormGroup>
                    <FormGroup className="mt-3">
                      <FormLabel htmlFor="magentoPostUrl">
                        Coupon Post URL
                      </FormLabel>
                      <FormControl
                        name="magentoPostUrl"
                        value={formik.values.magentoMetadata.postUrl}
                        onChange={(e) => {
                          formik.setFieldValue(
                            "magentoMetadata.postUrl",
                            e.target.value
                          );
                        }}
                        placeholder="https://example.com/checkout/cart/couponPost/"
                      />
                      <small className="form-text text-muted">
                        The coupon post URL required for Magento requests
                      </small>
                    </FormGroup>
                    <FormGroup className="mt-3">
                      <FormLabel htmlFor="userAgent">User Agent</FormLabel>
                      <FormControl
                        name="userAgent"
                        value={formik.values.magentoMetadata.userAgent}
                        onChange={(e) => {
                          formik.setFieldValue(
                            "magentoMetadata.userAgent",
                            e.target.value
                          );
                        }}
                        placeholder=""
                      />
                      <small className="form-text text-muted">
                        The User-Agent header to use for Magento requests
                      </small>
                    </FormGroup>
                  </Card.Body>
                </Collapse>
              </Card>
            )}

            {/* Salesforce Metadata Section */}
            {formik.values.checkoutType ===
              checkoutTechnologyType.SALESFORCE && (
              <Card className="mb-3">
                <Card.Header>
                  <Button
                    variant="link"
                    aria-controls="salesforce-settings"
                    aria-expanded={showSalesforce}
                    className="text-decoration-none p-0 text-start w-100"
                    onClick={() => setShowSalesforce(!showSalesforce)}
                  >
                    <h6 className="mb-0">
                      Salesforce Metadata
                      <i
                        className={`bi bi-chevron-${
                          showSalesforce ? "up" : "down"
                        } float-end`}
                      ></i>
                    </h6>
                  </Button>
                </Card.Header>
                <Collapse in={showSalesforce}>
                  <Card.Body id="salesforce-settings">
                    <FormGroup className="mt-3">
                      <FormLabel htmlFor="salesforceCsrfToken">
                        CSRF Token
                      </FormLabel>
                      <FormControl
                        name="salesforceCsrfToken"
                        value={formik.values.salesforceMetadata.csrfToken}
                        onChange={(e) => {
                          formik.setFieldValue(
                            "salesforceMetadata.csrfToken",
                            e.target.value
                          );
                        }}
                        placeholder="abc123def456"
                      />
                      <small className="form-text text-muted">
                        The CSRF token required for Salesforce Commerce Cloud
                        requests
                      </small>
                    </FormGroup>

                    <FormGroup className="mt-3">
                      <FormLabel htmlFor="salesforceCouponPostUrl">
                        Coupon Post URL
                      </FormLabel>
                      <FormControl
                        name="salesforceCouponPostUrl"
                        value={formik.values.salesforceMetadata.couponPostUrl}
                        onChange={(e) => {
                          formik.setFieldValue(
                            "salesforceMetadata.couponPostUrl",
                            e.target.value
                          );
                        }}
                        placeholder="/on/demandware.store/Sites-SiteName/default/Cart-AddCoupon"
                      />
                      <small className="form-text text-muted">
                        The URL path for applying coupons (e.g.
                        /on/demandware.store/Sites-*/default/Cart-AddCoupon)
                      </small>
                    </FormGroup>

                    <FormGroup className="mt-3">
                      <FormLabel htmlFor="salesforceReferer">Referer</FormLabel>
                      <FormControl
                        name="salesforceReferer"
                        value={formik.values.salesforceMetadata.referer}
                        onChange={(e) => {
                          formik.setFieldValue(
                            "salesforceMetadata.referer",
                            e.target.value
                          );
                        }}
                        placeholder="https://example.com/cart"
                      />
                      <small className="form-text text-muted">
                        The referer URL for Salesforce requests
                      </small>
                    </FormGroup>

                    <FormGroup className="mt-3">
                      <FormLabel htmlFor="salesforceOrigin">Origin</FormLabel>
                      <FormControl
                        name="salesforceOrigin"
                        value={formik.values.salesforceMetadata.origin}
                        onChange={(e) => {
                          formik.setFieldValue(
                            "salesforceMetadata.origin",
                            e.target.value
                          );
                        }}
                        placeholder="https://example.com"
                      />
                      <small className="form-text text-muted">
                        The origin URL for Salesforce requests
                      </small>
                    </FormGroup>

                    <FormGroup className="mt-3">
                      <FormLabel htmlFor="salesforceCookies">Cookies</FormLabel>
                      <FormControl
                        as="textarea"
                        rows={3}
                        name="salesforceCookies"
                        value={formik.values.salesforceMetadata.cookies}
                        onChange={(e) => {
                          formik.setFieldValue(
                            "salesforceMetadata.cookies",
                            e.target.value
                          );
                        }}
                        placeholder="dwsid=abc123; sid=def456; __cq_dnt=0"
                      />
                      <small className="form-text text-muted">
                        Session cookies required for Salesforce Commerce Cloud
                        requests
                      </small>
                    </FormGroup>
                    <FormGroup className="mt-3">
                      <FormLabel htmlFor="salesforceUserAgent">
                        User Agent
                      </FormLabel>
                      <FormControl
                        name="salesforceUserAgent"
                        value={formik.values.salesforceMetadata.userAgent}
                        onChange={(e) => {
                          formik.setFieldValue(
                            "salesforceMetadata.userAgent",
                            e.target.value
                          );
                        }}
                        placeholder=""
                      />
                      <small className="form-text text-muted">
                        The User-Agent header to use for Salesforce requests
                      </small>
                    </FormGroup>
                  </Card.Body>
                </Collapse>
              </Card>
            )}
          </div>
        </Col>
      </Row>

      {/* Selectors Settings Modal */}
      {merchant.data && (
        <SelectorsSettingsModal
          merchant={merchant.data}
          show={showSelectorsModal}
          onHide={() => setShowSelectorsModal(false)}
          onSave={(updatedSelectors) => {
            formik.setFieldValue(
              "discoveredSelectors",
              updatedSelectors.discoveredSelectors
            );
          }}
        />
      )}
    </div>
  );
};
